import apiClient from './apiClient';

const b = (clientId) => `/blotato/${clientId}`;

function sleep(ms) {
  return new Promise((res) => setTimeout(res, ms));
}

export async function getAccounts(clientId, platform) {
  const qs = platform ? `?platform=${platform}` : '';
  const { data } = await apiClient.get(`${b(clientId)}/accounts${qs}`);
  return data;
}

export async function getSubaccounts(clientId, accountId) {
  const { data } = await apiClient.get(`${b(clientId)}/accounts/${accountId}/subaccounts`);
  return data;
}

export async function getPinterestBoards(clientId, accountId) {
  const { data } = await apiClient.get(`${b(clientId)}/pinterest/boards?accountId=${accountId}`);
  return data;
}

export async function uploadLocalFile(clientId, file) {
  // Step 1: get presigned URL via backend
  const { data: { presignedUrl, publicUrl } } = await apiClient.post(
    `${b(clientId)}/media/uploads`,
    { filename: file.name }
  );
  // Step 2: PUT file directly to storage (CORS is fine here — storage bucket allows it)
  const putRes = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Presigned upload failed: ${putRes.status}`);
  return publicUrl;
}

export async function listVideoTemplates(clientId, search) {
  const qs = new URLSearchParams({ fields: 'id,name,description' });
  if (search) qs.set('search', search);
  const { data } = await apiClient.get(`${b(clientId)}/videos/templates?${qs}`);
  return data;
}

export async function createVideo(clientId, { templateId, prompt, inputs = {}, useBrandKit = false }) {
  if (!templateId) throw new Error('No video template available. Add templates in your Blotato account first.');
  const { data } = await apiClient.post(`${b(clientId)}/videos/from-templates`, {
    templateId,
    inputs,
    prompt,
    render: true,
    useBrandKit,
  });
  return data;
}

export async function pollVideo(clientId, id, { onStatus, intervalMs = 4000, timeoutMs = 300000 } = {}) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const { data } = await apiClient.get(`${b(clientId)}/videos/creations/${id}`);
    const status = data?.item?.status ?? data?.status;
    onStatus?.(status);
    if (status === 'done') return data?.item ?? data;
    if (status === 'creation-from-template-failed') throw new Error('Video creation failed');
    await sleep(intervalMs);
  }
  throw new Error('Video generation timed out');
}

export async function publishPost(clientId, body) {
  const { data } = await apiClient.post(`${b(clientId)}/posts`, body);
  return data;
}

export async function pollPost(clientId, postSubmissionId, { onStatus, intervalMs = 3000, timeoutMs = 120000 } = {}) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const { data } = await apiClient.get(`${b(clientId)}/posts/${postSubmissionId}`);
    onStatus?.(data.status);
    if (data.status === 'published') return data;
    if (data.status === 'failed') throw new Error(data.errorMessage ?? 'Post failed');
    await sleep(intervalMs);
  }
  throw new Error('Post publish timed out');
}
